import { Loader2, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router";
import Asterisk from "@/shared/components/Asterisk";
import { FileDropzone } from "@/shared/components/FileDropzone/FileDropzone";
import { zodFormResolver } from "@/shared/schemas/zodFormResolver";
import { postFile } from "@/shared/services/directUploads.service";
import { Button } from "@/shared/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/Dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/Form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/Select";
import { Textarea } from "@/shared/ui/TextArea";
import {
  SUGGESTION_CATEGORY_LABELS,
  SUGGESTION_SUBCATEGORIES,
} from "../configs/constants";
import { useCreateSuggestion, useUpdateSuggestion } from "../hooks/useSuggestionMutations";
import {
  suggestionSchema,
  type SuggestionFormValues,
} from "../schemas/suggestion.schema";
import type {
  SuggestionCategory,
  SuggestionSubcategory,
  UserSuggestion,
} from "../types/suggestions.types";

const ACCEPTED_FILE_TYPES = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

type SuggestionFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  suggestion?: UserSuggestion | null
};

const SuggestionFormDialog = ({
  open,
  onOpenChange,
  suggestion,
}: SuggestionFormDialogProps) => {
  const location = useLocation();
  const isEdit = !!suggestion;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { mutateAsync: createSuggestion, isPending: isCreating } =
    useCreateSuggestion();
  const { mutateAsync: updateSuggestion, isPending: isUpdating } =
    useUpdateSuggestion();

  const isSubmitting = isCreating || isUpdating;

  const form = useForm<SuggestionFormValues>({
    resolver: zodFormResolver(suggestionSchema),
    defaultValues: {
      category: suggestion?.category ?? undefined,
      subcategory: suggestion?.subcategory ?? "",
      message: suggestion?.message ?? "",
      attachment_signed_id: undefined,
    },
  });

  const selectedCategory = form.watch("category") as
    | SuggestionCategory
    | undefined;

  const subcategoryOptions = selectedCategory
    ? SUGGESTION_SUBCATEGORIES[selectedCategory]
    : [];

  // Reset subcategory when category changes
  useEffect(() => {
    if (!isEdit) {
      form.setValue("subcategory", "");
    }
  }, [selectedCategory, form, isEdit]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        category: suggestion?.category ?? undefined,
        subcategory: suggestion?.subcategory ?? "",
        message: suggestion?.message ?? "",
        attachment_signed_id: undefined,
      });
      setSelectedFile(null);
      setUploadError(null);
    }
  }, [open, suggestion, form]);

  const handleFileChange = useCallback(
    async (file: File | null) => {
      setSelectedFile(file);
      setUploadError(null);

      if (!file) {
        form.setValue("attachment_signed_id", undefined);
        return;
      }

      setIsUploading(true);
      try {
        const { signedId } = await postFile(file);
        form.setValue("attachment_signed_id", signedId, { shouldDirty: true });
      }
      catch {
        setUploadError(
          "Erreur lors du téléversement du fichier.",
        );
        setSelectedFile(null);
        form.setValue("attachment_signed_id", undefined);
      }
      finally {
        setIsUploading(false);
      }
    },
    [form],
  );

  const onSubmit = async (data: SuggestionFormValues) => {
    try {
      if (isEdit && suggestion) {
        await updateSuggestion({
          id: suggestion.id,
          payload: {
            message: data.message,
            category: data.category,
            subcategory: data.subcategory as SuggestionSubcategory,
            ...(data.attachment_signed_id && { attachment: data.attachment_signed_id }),
          },
        });
      }
      else {
        await createSuggestion({
          page_path: location.pathname,
          message: data.message,
          category: data.category,
          subcategory: data.subcategory as SuggestionSubcategory,
          ...(data.attachment_signed_id && { attachment: data.attachment_signed_id }),
        });
      }
      onOpenChange(false);
    }
    catch {
      // Errors are handled by mutation hooks
    }
  };

  const messageLength = form.watch("message")?.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Modifier la suggestion" : "Nouvelle suggestion"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            id="suggestion-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Catégorie
                    <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sélectionner une catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        {(
                          Object.entries(SUGGESTION_CATEGORY_LABELS) as Array<
                            [SuggestionCategory, string]
                          >
                        ).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subcategory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Sous-catégorie
                    <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!selectedCategory}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sélectionner une sous-catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        {subcategoryOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Message
                    <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-32"
                      placeholder="Décrivez votre suggestion en détail..."
                      rows={6}
                      {...field}
                    />
                  </FormControl>
                  <div className="flex justify-between">
                    <FormMessage />
                    <span className="text-xs text-muted-foreground ml-auto">
                      {messageLength}
                      /2000
                    </span>
                  </div>
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Pièce jointe (optionnel)</FormLabel>
              <FileDropzone
                title="Glissez ou cliquez pour ajouter un fichier"
                dragTitle="Déposez votre fichier ici..."
                subtitle="PNG, JPEG, GIF, WebP ou PDF — 5 Mo max"
                selectedFile={selectedFile ?? undefined}
                onChange={handleFileChange}
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_FILE_SIZE}
                isLoading={isUploading}
                loadingText="Téléversement en cours..."
                error={uploadError}
                disabled={isSubmitting || isUploading}
                success={!!form.getValues("attachment_signed_id") && !isUploading && !uploadError}
                dropzoneClassName="min-h-[120px]"
              />
            </FormItem>
          </form>
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            form="suggestion-form"
            variant="action"
            disabled={isSubmitting || isUploading}
          >
            {isSubmitting
              ? (
                <>
                  <Loader2 className="animate-spin" />
                  {isEdit ? "Modification..." : "Envoi..."}
                </>
              )
              : (
                <>
                  <Send />
                  {isEdit ? "Modifier" : "Envoyer"}
                </>
              )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestionFormDialog;
